import { EvaChartStates } from "types/factory";

import {
  BLUR_OPACITY,
  DEFAULT_WIDTH,
  DEFAULT_Z_INDEX,
  GAP_EDGE_WIDTH,
  HIGHLIGHTED_WIDTH,
  HIGHLIGHTED_Z_INDEX,
  isChartVisibleSeries
} from "../../translators/constants";
import {
  GapBridge,
  computeSelectionStates,
  getGapBridges,
  getGroupNameFromLabel
} from "../../translators/utils";

export function totalRateDateHighlightedSeries(states: Partial<EvaChartStates>) {
  const {
    series,
    layout,
    hoverLegendItem,
    hoverLegendGroup,
    attentionWells,
    selectedGroups = [],
    showWellCount
  } = states;

  const isOpaque = !(hoverLegendItem || selectedGroups.length > 0);

  // Must mirror the render translator's series filter (translateTotalRateDateResponseToOptions):
  // per-product count series are dropped from the chart, so the highlighted-series ids computed
  // here must match the rendered series' ids exactly. If they don't, useChartSelections' id-match
  // guard sees an unknown id and skips the highlight update entirely (EVA-6819 regression).
  // Both translators call the same isChartVisibleSeries predicate so the filters can't drift.
  // Gap bridges too, or the replaceMerge in useChartSelections deletes them on every highlight.
  const toHighlight = ({ id, group, source, kind }: GapBridge) => {
    if (kind === "spacer") {
      return { id, lineStyle: { width: DEFAULT_WIDTH, opacity: 0 }, z: DEFAULT_Z_INDEX };
    }
    const { isHovered } = computeSelectionStates({
      hoverLegendItem,
      hoverLegendGroup,
      attentionWells,
      label: source.label,
      groupTitle: group,
      selectedGroups
    });
    const z = isHovered ? HIGHLIGHTED_Z_INDEX : DEFAULT_Z_INDEX;
    if (kind === "edge") {
      return { id, lineStyle: { width: GAP_EDGE_WIDTH, opacity: 1 }, z };
    }
    return {
      id,
      lineStyle: {
        width: isHovered ? HIGHLIGHTED_WIDTH : source.style?.thickness ?? DEFAULT_WIDTH,
        opacity: isOpaque ? 1 : isHovered ? 1 : BLUR_OPACITY
      },
      z
    };
  };
  const bridges = getGapBridges(series, layout?.xAxis?.data ?? []);
  const gapEdges = bridges.filter((b) => b.kind === "edge").map(toHighlight);
  const gapBridges = bridges.filter((b) => b.kind !== "edge").map(toHighlight);

  return gapEdges
    .concat(
      series
        .filter((s) => isChartVisibleSeries(s.label, s.groupTitle, showWellCount))
        .map((s, index) => {
          const { label, style } = s;

          const group = getGroupNameFromLabel(label);

          const { isHovered } = computeSelectionStates({
            hoverLegendItem,
            hoverLegendGroup,
            attentionWells,
            label,
            groupTitle: group,
            selectedGroups
          });

          const id =
            group && group != label
              ? `${group}, ${label}, ${index}`
              : `${label}, ${index}`;

          return {
            id,
            lineStyle: {
              width: isHovered ? HIGHLIGHTED_WIDTH : style?.thickness ?? DEFAULT_WIDTH,
              opacity: isOpaque ? 1 : isHovered ? 1 : BLUR_OPACITY
            },

            z: isHovered ? HIGHLIGHTED_Z_INDEX : DEFAULT_Z_INDEX
          };
        })
    )
    .concat(gapBridges);
}
